/**
 * Program affiliation panel (Sheepdog) on the business page (Business Account Pages slice 2, migration 315).
 * Records that the business participates in a program, with a state (Proposed / Confirmed / Ended) and where we know
 * it from. It is NOT coverage and NOT membership: it changes nothing for the business's people, their memberships,
 * journeys, households or check-ins. Dates are stamped by the server.
 */

import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Card from "../ui/Card";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { useConfirmDialog } from "../ui/ConfirmDialog";
import { formatDateMDY } from "../../utils/formatters";
import {
  AFFILIATION_SOURCES,
  AFFILIATION_STATUSES,
  PROGRAMS,
  deleteProgramAffiliation,
  getProgramAffiliations,
  programLabel,
  saveProgramAffiliation,
  sourceLabel,
  statusLabel,
} from "../../services/organizationPrograms.service";
import type {
  AffiliationInput,
  ProgramAffiliation,
} from "../../services/organizationPrograms.service";

export const affiliationChipClass = (status: string): string =>
  status === "confirmed"
    ? "bg-green-100 text-green-800"
    : status === "proposed"
      ? "bg-sky-100 text-sky-800"
      : "bg-gray-100 text-gray-600";

interface Props {
  organizationId: string;
  canEdit: boolean;
}

const ProgramAffiliationPanel: React.FC<Props> = ({
  organizationId,
  canEdit,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["program-affiliations", organizationId],
    queryFn: () => getProgramAffiliations(organizationId),
  });
  const [editing, setEditing] = useState<ProgramAffiliation | "new" | null>(
    null,
  );
  const [form, setForm] = useState<AffiliationInput>({
    program: "sheepdog",
    status: "proposed",
    source: "meeting",
    reference: "",
    notes: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["program-affiliations", organizationId],
    });

  const save = useMutation({
    mutationFn: () =>
      saveProgramAffiliation(
        organizationId,
        form,
        editing && editing !== "new" ? editing.id : undefined,
      ),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      showToast("success", "Program affiliation saved");
    },
    onError: (e: Error) =>
      setErr(
        e.message.includes("org_program_key")
          ? "This business already has an affiliation for that program."
          : e.message.includes("row-level security")
            ? "You do not have permission to change program affiliations."
            : e.message,
      ),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteProgramAffiliation(id),
    onSuccess: () => {
      invalidate();
      showToast("success", "Program affiliation removed");
    },
    onError: (e: Error) => showToast("error", e.message),
  });

  const open = (row: ProgramAffiliation | "new") => {
    setErr(null);
    setForm(
      row === "new"
        ? {
            program: "sheepdog",
            status: "proposed",
            source: "meeting",
            reference: "",
            notes: "",
          }
        : {
            program: row.program,
            status: row.status,
            source: row.source,
            reference: row.reference ?? "",
            notes: row.notes ?? "",
          },
    );
    setEditing(row);
  };

  const available = PROGRAMS.filter(
    (p) => !rows.some((r) => r.program === p.value),
  );
  const sel = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";

  return (
    <Card>
      <div
        className="flex flex-wrap items-center justify-between gap-2 mb-2"
        data-testid="section-programs"
      >
        <h3 className="text-lg font-semibold text-gray-900">
          Program Affiliation
        </h3>
        {canEdit && !editing && available.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => open("new")}
            data-testid="add-program"
          >
            Add Sheepdog affiliation
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Records a business partnership with a program. It is not coverage and
        does not change anyone&apos;s membership.
      </p>

      {editing ? (
        <div className="space-y-4" data-testid="program-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="aff-program"
              >
                Program
              </label>
              <select
                id="aff-program"
                className={sel}
                value={form.program}
                disabled={editing !== "new"}
                onChange={(e) =>
                  setForm({
                    ...form,
                    program: e.target.value as AffiliationInput["program"],
                  })
                }
              >
                {PROGRAMS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="aff-status"
              >
                State
              </label>
              <select
                id="aff-status"
                className={sel}
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value as AffiliationInput["status"],
                  })
                }
              >
                {AFFILIATION_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label} — {s.hint}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="aff-source"
              >
                Source
              </label>
              <select
                id="aff-source"
                className={sel}
                value={form.source}
                onChange={(e) =>
                  setForm({
                    ...form,
                    source: e.target.value as AffiliationInput["source"],
                  })
                }
              >
                {AFFILIATION_SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="aff-ref"
            >
              Reference
            </label>
            <input
              id="aff-ref"
              className={sel}
              maxLength={500}
              placeholder="Agreement number, ticket or link"
              value={form.reference ?? ""}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="aff-notes"
            >
              Notes
            </label>
            <textarea
              id="aff-notes"
              rows={2}
              maxLength={2000}
              className={sel}
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          {err && (
            <div
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {err}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setEditing(null)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} isLoading={save.isPending}>
              Save
            </Button>
          </div>
        </div>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No program affiliation.</p>
      ) : (
        <ul className="divide-y">
          {rows.map((r) => (
            <li
              key={r.id}
              className="py-3 flex flex-wrap items-start justify-between gap-3"
              data-testid="program-row"
            >
              <div className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">
                    {programLabel(r.program)}
                  </span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${affiliationChipClass(r.status)}`}
                    data-testid="program-status"
                  >
                    {statusLabel(r.status)}
                  </span>
                </div>
                <div className="mt-1 text-gray-600">
                  Source: {sourceLabel(r.source)}
                  {r.reference && (
                    <>
                      {" "}
                      · Reference:{" "}
                      <span className="break-all">{r.reference}</span>
                    </>
                  )}
                </div>
                <div className="text-gray-500">
                  Proposed {formatDateMDY(r.proposed_at)}
                  {r.confirmed_at && (
                    <> · Confirmed {formatDateMDY(r.confirmed_at)}</>
                  )}
                  {r.ended_at && <> · Ended {formatDateMDY(r.ended_at)}</>}
                </div>
                {r.notes && (
                  <div className="mt-1 whitespace-pre-line text-gray-700">
                    {r.notes}
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="flex gap-3 text-sm">
                  <button
                    className="text-indigo-600 hover:text-indigo-900"
                    onClick={() => open(r)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: "Remove program affiliation?",
                          message:
                            "This deletes the record. To keep history, set the state to Ended instead.",
                          confirmText: "Remove",
                          variant: "danger",
                        })
                      ) {
                        remove.mutate(r.id);
                      }
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialogComponent />
    </Card>
  );
};

export default ProgramAffiliationPanel;
